import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { useIsApiReady } from "../hooks/use-api-ready";
import {
  UpdateKitBodyDto,
  groupKitControllerCreateKit,
  groupKitControllerDeleteKit,
  groupKitControllerDeletionPreview,
  groupKitControllerGetKit,
  groupKitControllerGetKits,
  groupKitControllerUpdateKit,
  groupKitExpertLinkControllerLink,
  groupKitExpertLinkControllerUnlink,
  groupKitFolderLinkControllerLink,
  groupKitFolderLinkControllerUnlink,
  groupKitSkillLinkControllerLink,
  groupKitSkillLinkControllerUnlink,
  groupKitTemplateLinkControllerLink,
  groupKitTemplateLinkControllerUnlink,
  kitCollectionControllerLink,
  kitCollectionControllerUnlink,
  type GroupKitControllerGetKitsData,
} from "../lib/gen-api";
import { queryKeys } from "../lib/query-keys";

type KitsFilter = {
  type?: NonNullable<GroupKitControllerGetKitsData["query"]>["filter[type]"];
  owned?: NonNullable<GroupKitControllerGetKitsData["query"]>["filter[owned]"];
  shared?: NonNullable<
    GroupKitControllerGetKitsData["query"]
  >["filter[shared]"];
  isPublished?: NonNullable<
    GroupKitControllerGetKitsData["query"]
  >["filter[isPublished]"];
};

const requestKitDeletion = async (groupUuid: string, kitUuid: string) => {
  const result = await groupKitControllerDeleteKit({
    path: { groupUuid, kitUuid },
  });

  if (!result.response.ok) {
    throw new Error("Failed to delete kit");
  }
};

const invalidateAfterKitDeletion = (
  queryClient: QueryClient,
  workspaceUuid: string,
  kitUuid: string,
) => {
  const deletedKitKey = queryKeys.kit(workspaceUuid, kitUuid);

  void queryClient.invalidateQueries({
    queryKey: queryKeys.group(),
    predicate: ({ queryKey }) =>
      !deletedKitKey.every((part, index) => queryKey[index] === part),
  });
};

export const useKitsData = (groupUuid: string | null, filter?: KitsFilter) => {
  const queryClient = useQueryClient();

  const {
    data: kits,
    isPending,
    error,
  } = useQuery({
    enabled: useIsApiReady() && !!groupUuid,
    queryKey: queryKeys.kits(groupUuid!, filter),
    queryFn: () =>
      groupKitControllerGetKits({
        path: {
          groupUuid: groupUuid!,
        },
        query: {
          filter: {
            type: filter?.type,
            owned: filter?.owned,
            shared: filter?.shared,
            isPublished: filter?.isPublished,
          },
        },
      }).then((res) => res.data?.data ?? []),
  });

  const { mutateAsync: createKit, isPending: isCreatingKit } = useMutation({
    mutationFn: ({
      name,
      presetKitUuid,
      type,
      expertUuids,
      folderUuids,
    }: {
      name: string;
      presetKitUuid?: string;
      type?: KitsFilter["type"];
      expertUuids?: string[];
      folderUuids?: string[];
    }) => {
      if (!groupUuid) {
        throw new Error("Group UUID is required");
      }

      if (!filter?.type) {
        throw new Error("Kit type is required to create a kit");
      }

      return groupKitControllerCreateKit({
        path: {
          groupUuid,
        },
        body: {
          name,
          type: type ?? filter.type,
          preset_kit_uuid: presetKitUuid,
          expert_uuids: expertUuids,
          folder_uuids: folderUuids,
        },
      }).then((res) => res.data);
    },
    onSettled: (_data, _error, { type }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kits(groupUuid!, filter),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.kits(
          groupUuid!,
          type
            ? {
                type: type,
              }
            : filter,
        ),
      });
    },
  });

  const { mutateAsync: deleteKit } = useMutation({
    mutationFn: async (vars: { kitUuid: string }) => {
      if (!groupUuid) {
        throw new Error("Group UUID is required");
      }

      return requestKitDeletion(groupUuid, vars.kitUuid);
    },
    onSuccess: (_data, vars) => {
      invalidateAfterKitDeletion(queryClient, groupUuid!, vars.kitUuid);
    },
  });

  const { mutateAsync: updateKit } = useMutation({
    mutationFn: async (vars: UpdateKitBodyDto & { kitUuid: string }) => {
      if (!groupUuid) {
        throw new Error("Group UUID is required");
      }

      return groupKitControllerUpdateKit({
        path: {
          groupUuid,
          kitUuid: vars.kitUuid,
        },
        body: {
          name: vars.name,
          description: vars.description,
          type: vars.type,
        },
      });
    },
    onSettled: (_data, _error, vars) => {
      // Invalidate the specific filtered query
      queryClient.invalidateQueries({
        queryKey: queryKeys.kits(groupUuid!, filter),
      });

      // Also invalidate all kits queries for this group
      queryClient.invalidateQueries({
        queryKey: queryKeys.kits(groupUuid!),
      });

      queryClient.invalidateQueries({
        queryKey: queryKeys.kit(groupUuid!, vars.kitUuid),
      });
    },
  });

  return {
    kits: kits ?? [],
    isLoading: isPending,
    error,
    createKit,
    deleteKit,
    updateKit,
    isCreatingKit,
  };
};

export function useKitData(groupUuid: string | null, kitUuid: string | null) {
  const queryClient = useQueryClient();

  const { data: kit, isPending } = useQuery({
    enabled: useIsApiReady() && !!groupUuid && !!kitUuid,
    initialData: undefined,
    queryKey: queryKeys.kit(groupUuid!, kitUuid!),
    queryFn: () =>
      groupKitControllerGetKit({
        path: {
          groupUuid: groupUuid!,
          kitUuid: kitUuid!,
        },
      }).then((res) => res.data),
  });

  const { mutateAsync: updateKit } = useMutation({
    mutationFn: async (body: UpdateKitBodyDto) => {
      if (!groupUuid) {
        throw new Error("Group UUID is required");
      }

      return groupKitControllerUpdateKit({
        path: {
          groupUuid,
          kitUuid: kitUuid!,
        },
        body,
      });
    },
    onSettled: (_data, _error) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kits(groupUuid!),
      });

      queryClient.invalidateQueries({
        queryKey: queryKeys.kit(groupUuid!, kitUuid!),
      });
    },
  });

  const { mutateAsync: deleteKit, isPending: isDeletingKit } = useMutation({
    mutationFn: async (kitUuid: string) => {
      if (!groupUuid) {
        throw new Error("Group UUID is required");
      }

      return requestKitDeletion(groupUuid, kitUuid);
    },
    onSuccess: (_data, deletedKitUuid) => {
      invalidateAfterKitDeletion(queryClient, groupUuid!, deletedKitUuid);
    },
  });

  // ============================================================
  // Links
  // ============================================================

  const { mutateAsync: linkExpert } = useMutation({
    mutationFn: async (vars: { expertUuid: string }) => {
      if (!groupUuid) {
        throw new Error("Group UUID is required");
      }

      if (!kitUuid) {
        throw new Error("Kit UUID is required");
      }

      return groupKitExpertLinkControllerLink({
        path: {
          groupUuid,
          kitUuid,
          expertUuid: vars.expertUuid,
        },
        throwOnError: true,
      });
    },
    onSettled: (_data, _error) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitExperts(groupUuid!, kitUuid!),
      });
    },
  });

  const { mutateAsync: unlinkExpert } = useMutation({
    mutationFn: async (vars: { expertUuid: string }) => {
      if (!groupUuid) {
        throw new Error("Group UUID is required");
      }

      if (!kitUuid) {
        throw new Error("Kit UUID is required");
      }

      return groupKitExpertLinkControllerUnlink({
        path: {
          groupUuid,
          kitUuid,
          expertUuid: vars.expertUuid,
        },
      });
    },
    onSettled: (_data, _error) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitExperts(groupUuid!, kitUuid!),
      });
    },
  });

  const { mutateAsync: linkSkill } = useMutation({
    mutationFn: async (vars: { skillUuid: string }) => {
      if (!groupUuid) {
        throw new Error("Group UUID is required");
      }

      if (!kitUuid) {
        throw new Error("Kit UUID is required");
      }

      return groupKitSkillLinkControllerLink({
        path: {
          groupUuid,
          kitUuid,
          skillUuid: vars.skillUuid,
        },
      });
    },
    onSettled: (_data, _error) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitSkills(groupUuid!, kitUuid!),
      });
    },
  });

  const { mutateAsync: unlinkSkill } = useMutation({
    mutationFn: async (vars: { skillUuid: string }) => {
      if (!groupUuid) {
        throw new Error("Group UUID is required");
      }

      if (!kitUuid) {
        throw new Error("Kit UUID is required");
      }

      return groupKitSkillLinkControllerUnlink({
        path: {
          groupUuid,
          kitUuid,
          skillUuid: vars.skillUuid,
        },
      });
    },
    onSettled: (_data, _error) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitSkills(groupUuid!, kitUuid!),
      });
    },
  });

  const { mutateAsync: linkTemplate } = useMutation({
    mutationFn: async (vars: { templateUuid: string }) => {
      if (!groupUuid) {
        throw new Error("Group UUID is required");
      }

      if (!kitUuid) {
        throw new Error("Kit UUID is required");
      }

      return groupKitTemplateLinkControllerLink({
        path: {
          groupUuid,
          kitUuid,
          templateUuid: vars.templateUuid,
        },
      });
    },
    onSettled: (_data, _error) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitTemplates(groupUuid!, kitUuid!),
      });
    },
  });

  const { mutateAsync: unlinkTemplate } = useMutation({
    mutationFn: async (vars: { templateUuid: string }) => {
      if (!groupUuid) {
        throw new Error("Group UUID is required");
      }

      if (!kitUuid) {
        throw new Error("Kit UUID is required");
      }

      return groupKitTemplateLinkControllerUnlink({
        path: {
          groupUuid,
          kitUuid,
          templateUuid: vars.templateUuid,
        },
      });
    },
    onSettled: (_data, _error) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitTemplates(groupUuid!, kitUuid!),
      });
    },
  });

  const { mutateAsync: linkFolder } = useMutation({
    mutationFn: async (vars: { folderUuid: string }) => {
      if (!groupUuid) {
        throw new Error("Group UUID is required");
      }

      if (!kitUuid) {
        throw new Error("Kit UUID is required");
      }

      return groupKitFolderLinkControllerLink({
        path: {
          groupUuid,
          kitUuid,
          folderUuid: vars.folderUuid,
        },
      });
    },
    onSettled: (_data, _error) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitFolders(groupUuid!, kitUuid!),
      });
    },
  });

  const { mutateAsync: unlinkFolder } = useMutation({
    mutationFn: async (vars: { folderUuid: string }) => {
      if (!groupUuid) {
        throw new Error("Group UUID is required");
      }

      if (!kitUuid) {
        throw new Error("Kit UUID is required");
      }

      return groupKitFolderLinkControllerUnlink({
        path: {
          groupUuid,
          kitUuid,
          folderUuid: vars.folderUuid,
        },
      });
    },
    onSettled: (_data, _error) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitFolders(groupUuid!, kitUuid!),
      });
    },
  });

  const { mutateAsync: linkCollection } = useMutation({
    mutationFn: async (vars: { collectionUuid: string }) => {
      if (!kitUuid) {
        throw new Error("Kit UUID is required");
      }

      return kitCollectionControllerLink({
        body: {
          kitUuid,
          collectionUuid: vars.collectionUuid,
        },
      });
    },
    onSettled: (_data, _error) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitCollections(groupUuid!, kitUuid!),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitExperts(groupUuid!, kitUuid!),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitFolders(groupUuid!, kitUuid!),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitTemplates(groupUuid!, kitUuid!),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitSkills(groupUuid!, kitUuid!),
      });
    },
  });

  const { mutateAsync: unlinkCollection } = useMutation({
    mutationFn: async (vars: { collectionUuid: string }) => {
      if (!kitUuid) {
        throw new Error("Kit UUID is required");
      }

      return kitCollectionControllerUnlink({
        body: {
          kitUuid,
          collectionUuid: vars.collectionUuid,
        },
      });
    },
    onSettled: (_data, _error) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitCollections(groupUuid!, kitUuid!),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitExperts(groupUuid!, kitUuid!),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitFolders(groupUuid!, kitUuid!),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitTemplates(groupUuid!, kitUuid!),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitSkills(groupUuid!, kitUuid!),
      });
    },
  });

  return {
    kit,
    isLoading: isPending,
    updateKit,
    linkExpert,
    unlinkExpert,
    linkSkill,
    unlinkSkill,
    linkTemplate,
    unlinkTemplate,
    linkFolder,
    unlinkFolder,
    linkCollection,
    unlinkCollection,
    deleteKit,
    isDeletingKit,
  };
}

export function useKitDeletionPreview(
  groupUuid: string | null,
  kitUuid: string | null,
) {
  const { data: deletionPreview, isLoading } = useQuery({
    queryKey: queryKeys.kitDeletionPreview(groupUuid!, kitUuid!),
    queryFn: () =>
      groupKitControllerDeletionPreview({
        path: {
          groupUuid: groupUuid!,
          kitUuid: kitUuid!,
        },
      }).then((res) => res.data),
    enabled: useIsApiReady() && !!groupUuid && !!kitUuid,
  });

  return { deletionPreview, isLoading };
}
